import { useState } from "react";
import axios from "axios";
import { Logo } from "../components/Logo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  "find-property": "Find My Property",
  inventory: "Today's Inventory",
  "site-visit": "Site Visit",
  advisor: "Advisor Request",
  "request-price": "Price Request",
  "yeida-alert": "YEIDA Scheme Alert",
  "7th-parkview-enquiry": "7th Park View Enquiry",
  "yeida-sector-enquiry": "YEIDA Sector Enquiry",
  "ace-yamuna-enquiry": "Ace Group Enquiry",
  "purvanchal-yamuna-enquiry": "Purvanchal/Other Enquiry",
  "shortlist-review": "Shortlist Review",
};

const CATS = ["Apartment", "Plot", "Villa", "Commercial", "Studio", "Resale"];
const LISTING_INITIAL = { project: "", category: "Apartment", config: "", size: "", price: "", price_value: "", status: "Under Development", listing: "New", possession: "" };

function AdminListingForm({ apiKey }) {
  const [form, setForm] = useState(LISTING_INITIAL);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, price: form.price || "Price on Request", price_value: form.price_value ? Number(form.price_value) : null };
      const res = await axios.post(`${API}/listings`, payload, { params: { key: apiKey } });
      toast.success(`Listing "${res.data.project}" published. Matching saved-search alerts have been emailed.`);
      setForm(LISTING_INITIAL);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not publish listing.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-10 border border-[#D4AF37]/20 bg-[#111111] rounded-[2px]" data-testid="admin-listing-panel">
      <button type="button" data-testid="admin-listing-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 text-left">
        <span><span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-1">Publish New Listing</span><span className="font-serif-lux text-xl text-[#F5F5F0]">Add inventory &amp; trigger saved-search alerts</span></span>
        <span className="text-[#D4AF37] text-xl">{open ? "-" : "+"}</span>
      </button>
      {open && (
        <form onSubmit={submit} className="px-6 pb-6 grid sm:grid-cols-3 gap-3" data-testid="admin-listing-form">
          <input data-testid="listing-project-input" className="lux-input" placeholder="Project name *" value={form.project} onChange={set("project")} required minLength={2} />
          <select data-testid="listing-category-select" className="lux-input" value={form.category} onChange={set("category")}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
          <input data-testid="listing-config-input" className="lux-input" placeholder="Configuration (e.g. 3 BHK)" value={form.config} onChange={set("config")} />
          <input data-testid="listing-size-input" className="lux-input" placeholder="Size (e.g. 1,675 sq.ft)" value={form.size} onChange={set("size")} />
          <input data-testid="listing-price-input" className="lux-input" placeholder="Price label (e.g. Rs 1.05 Cr)" value={form.price} onChange={set("price")} />
          <input data-testid="listing-price-value-input" type="number" className="lux-input" placeholder="Price in rupees (for budget matching)" value={form.price_value} onChange={set("price_value")} />
          <select data-testid="listing-status-select" className="lux-input" value={form.status} onChange={set("status")}>{["Operational", "Under Development", "Announced"].map((s) => <option key={s}>{s}</option>)}</select>
          <select data-testid="listing-listing-select" className="lux-input" value={form.listing} onChange={set("listing")}>{["New", "Resale"].map((s) => <option key={s}>{s}</option>)}</select>
          <input data-testid="listing-possession-input" className="lux-input" placeholder="Possession (e.g. Ready / 2027)" value={form.possession} onChange={set("possession")} />
          <button data-testid="listing-submit-btn" disabled={busy} className="sm:col-span-3 py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black rounded-[2px] disabled:opacity-50">
            {busy ? "Publishing..." : "Publish Listing & Send Alerts"}
          </button>
        </form>
      )}
    </div>
  );
}

function AdminDigestPanel({ apiKey }) {
  const [news, setNews] = useState({ title: "", summary: "", link: "", status: "Announced" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [last, setLast] = useState(null);
  const set = (k) => (e) => setNews({ ...news, [k]: e.target.value });

  const publishNews = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/news`, { ...news, link: news.link || null }, { params: { key: apiKey } });
      toast.success("Corridor news item added to this week's digest.");
      setNews({ title: "", summary: "", link: "", status: "Announced" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add news item.");
    } finally {
      setBusy(false);
    }
  };

  const runDigest = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/digest/run`, null, { params: { key: apiKey } });
      setLast(res.data);
      toast.success(`Digest sent to ${res.data.sent} customer${res.data.sent === 1 ? "" : "s"} (${res.data.skipped} skipped).`);
    } catch {
      toast.error("Digest run failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-10 border border-[#D4AF37]/20 bg-[#111111] rounded-[2px]" data-testid="admin-digest-panel">
      <button type="button" data-testid="admin-digest-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 text-left">
        <span><span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-1">Friday Digest</span><span className="font-serif-lux text-xl text-[#F5F5F0]">Corridor news &amp; weekly email (auto-sends Fridays 09:00 IST)</span></span>
        <span className="text-[#D4AF37] text-xl">{open ? "-" : "+"}</span>
      </button>
      {open && (
        <div className="px-6 pb-6">
          <form onSubmit={publishNews} className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="admin-news-form">
            <input data-testid="news-title-input" className="lux-input" placeholder="News headline *" value={news.title} onChange={set("title")} required minLength={3} />
            <select data-testid="news-status-select" className="lux-input" value={news.status} onChange={set("status")}>{["Operational", "Under Development", "Announced", "Proposed"].map((s) => <option key={s}>{s}</option>)}</select>
            <textarea data-testid="news-summary-input" className="lux-input sm:col-span-2" rows={2} placeholder="One-paragraph summary *" value={news.summary} onChange={set("summary")} required minLength={3} />
            <input data-testid="news-link-input" className="lux-input" placeholder="Source link (https://...) optional" value={news.link} onChange={set("link")} />
            <button data-testid="news-submit-btn" disabled={busy} className="py-3 text-[11px] font-semibold uppercase tracking-[0.2em] border border-[#D4AF37]/50 text-[#F3E5AB] hover:bg-[#D4AF37]/10 rounded-[2px] disabled:opacity-50">Add News Item</button>
          </form>
          <div className="flex flex-wrap items-center gap-4">
            <button type="button" data-testid="digest-run-btn" onClick={runDigest} disabled={busy} className="px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black rounded-[2px] disabled:opacity-50">
              {busy ? "Working..." : "Send Digest Now"}
            </button>
            {last && <p className="text-[12px] text-[#A1A1AA]" data-testid="digest-run-result">Sent {last.sent} &middot; skipped {last.skipped} &middot; {last.listings_in_window} listings &amp; {last.news_in_window} news items in the last 7 days</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState("");
  const [leads, setLeads] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.get(`${API}/leads`, { params: { key } });
      setLeads(res.data);
    } catch {
      toast.error("Invalid access key.");
      setLeads(null);
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    if (!leads?.length) return;
    const header = ["Created", "Type", "Name", "Phone", "WhatsApp", "Budget", "Property Type", "Purpose", "Configuration", "Timeline", "Message"];
    const rows = leads.map((l) => [l.created_at, LABELS[l.lead_type] || l.lead_type, l.name, l.phone, l.whatsapp, l.budget, l.property_type, l.purpose, l.configuration, l.timeline, l.message]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "radiance-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-5 py-12" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <Logo />
          <a href="/" data-testid="admin-back-link" className="text-[11px] uppercase tracking-[0.25em] text-[#71717A] hover:text-[#F3E5AB] transition-colors">Back to Site</a>
        </div>

        {leads === null ? (
          <form onSubmit={load} className="max-w-md mx-auto mt-24 border border-[#D4AF37]/20 bg-[#111111] rounded-[2px] p-8" data-testid="admin-login-form">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Advisor Desk</p>
            <h1 className="font-serif-lux text-3xl text-[#F5F5F0] mb-6">Captured Leads</h1>
            <input
              data-testid="admin-key-input"
              type="password"
              className="lux-input mb-4"
              placeholder="Access Key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
            <button data-testid="admin-login-btn" disabled={busy} className="w-full py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black rounded-[2px] disabled:opacity-50">
              {busy ? "Loading..." : "View Leads"}
            </button>
          </form>
        ) : (
          <>
            <AdminListingForm apiKey={key} />
            <AdminDigestPanel apiKey={key} />
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-serif-lux text-3xl text-[#F5F5F0]" data-testid="admin-leads-title">
                Leads <span className="text-[#D4AF37]">({leads.length})</span>
              </h1>
              <button data-testid="admin-export-btn" onClick={exportCsv} className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] border border-[#D4AF37]/50 text-[#F3E5AB] hover:bg-[#D4AF37]/10 rounded-[2px] transition-colors">
                Export CSV
              </button>
            </div>
            {leads.length === 0 ? (
              <p className="text-[#71717A] text-sm" data-testid="admin-empty">No leads captured yet.</p>
            ) : (
              <div className="overflow-x-auto border border-[#D4AF37]/15 rounded-[2px]" data-testid="admin-leads-table">
                <table className="w-full text-sm min-w-[900px]">
                  <thead>
                    <tr className="text-left border-b border-[#D4AF37]/20 text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                      {["Date", "Type", "Name", "Phone", "Budget", "Property", "Purpose", "Timeline", "Message"].map((h) => (
                        <th key={h} className="py-3 px-4 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} className="border-b border-[#D4AF37]/8 hover:bg-[#D4AF37]/[0.03]" data-testid={`admin-lead-row-${l.id}`}>
                        <td className="py-3 px-4 text-[#71717A] whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-IN")}</td>
                        <td className="py-3 px-4 text-[#D4AF37] whitespace-nowrap">{LABELS[l.lead_type] || l.lead_type}</td>
                        <td className="py-3 px-4 text-[#F5F5F0]">{l.name}</td>
                        <td className="py-3 px-4 text-[#A1A1AA]">{l.phone}</td>
                        <td className="py-3 px-4 text-[#A1A1AA]">{l.budget || "-"}</td>
                        <td className="py-3 px-4 text-[#A1A1AA]">{l.property_type || "-"}</td>
                        <td className="py-3 px-4 text-[#A1A1AA]">{l.purpose || "-"}</td>
                        <td className="py-3 px-4 text-[#A1A1AA]">{l.timeline || "-"}</td>
                        <td className="py-3 px-4 text-[#71717A] max-w-[220px] truncate">{l.message || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
